import { useEffect, useState } from "react";
import { Link2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

const KEYS = ["internal_link_similarity_threshold", "internal_link_max_per_article"] as const;

const InternalLinkSettings = () => {
  const [threshold, setThreshold] = useState("0.75");
  const [maxLinks, setMaxLinks] = useState("6");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select("key,value")
        .in("key", KEYS as unknown as string[]);
      if (!error && data) {
        const m = new Map(data.map((r) => [r.key, r.value]));
        if (m.get("internal_link_similarity_threshold")) setThreshold(m.get("internal_link_similarity_threshold")!);
        if (m.get("internal_link_max_per_article")) setMaxLinks(m.get("internal_link_max_per_article")!);
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    const t = Number(threshold);
    const n = parseInt(maxLinks, 10);
    if (!Number.isFinite(t) || t < 0 || t > 1) {
      toast({ title: "Invalid threshold", description: "Similarity threshold must be between 0 and 1.", variant: "destructive" });
      return;
    }
    if (!Number.isFinite(n) || n < 1 || n > 20) {
      toast({ title: "Invalid limit", description: "Max links per article must be between 1 and 20.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("app_config").upsert(
      [
        { key: "internal_link_similarity_threshold", value: String(t) },
        { key: "internal_link_max_per_article", value: String(n) },
      ],
      { onConflict: "key" },
    );
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Settings saved", description: "Internal-link rules updated." });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Link2 size={18} style={{ color: "hsl(42 55% 65%)" }} />
          <h3 className="text-lg font-semibold" style={{ color: "hsl(0 0% 95%)" }}>Internal Link Settings</h3>
        </div>
        <p className="text-sm" style={{ color: "hsl(0 0% 60%)" }}>
          Controls the pgvector similarity threshold and the maximum number of internal links generated per article.
        </p>
      </div>

      <div className="rounded-lg border p-5 space-y-5 max-w-xl"
        style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 16%)" }}>
        <div className="space-y-2">
          <Label htmlFor="threshold" style={{ color: "hsl(0 0% 85%)" }}>
            Similarity threshold (0, 1)
          </Label>
          <Input
            id="threshold"
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={threshold}
            disabled={loading}
            onChange={(e) => setThreshold(e.target.value)}
          />
          <p className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>
            Minimum cosine similarity required for a related article to be suggested. Higher = stricter (e.g. 0.80). Lower = more matches (e.g. 0.65).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="maxlinks" style={{ color: "hsl(0 0% 85%)" }}>
            Max internal links per article (1, 20)
          </Label>
          <Input
            id="maxlinks"
            type="number"
            min={1}
            max={20}
            step={1}
            value={maxLinks}
            disabled={loading}
            onChange={(e) => setMaxLinks(e.target.value)}
          />
          <p className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>
            Hard cap applied to both manual suggestions and the “Auto-link related” action in the Publish tab.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || loading}>
            <Save size={14} /> {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InternalLinkSettings;
